import { useState } from "react";
import type { Insight, InsightSeverity } from "../../api/insights";
import { formatScore, riskLevelColor, riskLevelLabel, riskLevelTint } from "../../lib/riskVocabulary";

/** Shared building blocks for the executive cockpit lenses. */

export const NAVY = "#0B1F3A";

export const SEVERITY: Record<InsightSeverity, { label: string; color: string; tint: string }> = {
  critical: { label: "Critical", color: riskLevelColor("Critical"), tint: riskLevelTint("Critical") },
  high: { label: "High", color: riskLevelColor("High"), tint: riskLevelTint("High") },
  medium: { label: "Medium", color: riskLevelColor("Moderate"), tint: riskLevelTint("Moderate") },
  low: { label: "Low", color: riskLevelColor("Low"), tint: riskLevelTint("Low") },
  info: { label: "Info", color: "#475569", tint: "#f1f5f9" },
};

export function Panel({ title, subtitle, children, action, testId }: { title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode; testId?: string }) {
  return (
    <section className="bg-white border border-ink-100 rounded-sm p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
          {subtitle && <p className="text-xs text-ink-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Kpi({ label, value, unit, sub, note, accent, title, testId }: { label: string; value: string; unit?: string; sub?: string; note?: string; accent?: string; title?: string; testId?: string }) {
  return (
    <div className="bg-white border border-ink-100 rounded-sm px-4 py-3" title={title} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 whitespace-nowrap">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className="text-[28px] leading-none font-semibold tabular-nums" style={{ color: accent ?? NAVY }}>{value}</span>
        {unit && <span className="text-xs text-ink-500">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-ink-500 mt-1.5 leading-snug">{sub}</div>}
      {note && <div className="text-[11px] text-ink-700 mt-1 leading-snug" data-testid={testId ? `${testId}-note` : undefined}>{note}</div>}
    </div>
  );
}

export function SeverityDot({ severity }: { severity: InsightSeverity }) {
  const s = SEVERITY[severity];
  return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ background: s.color }} aria-label={s.label} title={s.label} />;
}

export function ConfidenceChip({ level, basis }: { level: string; basis: string }) {
  const color = level === "High" ? riskLevelColor("Very_low") : level === "Moderate" ? riskLevelColor("Moderate") : riskLevelColor("High");
  return (
    <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-sm border whitespace-nowrap" style={{ color, borderColor: `${color}55` }} title={basis}>
      {level} confidence
    </span>
  );
}

export function LevelBadge({ level, score }: { level: string | null; score?: number | null }) {
  if (!level) return <span className="text-xs text-ink-400">—</span>;
  const c = riskLevelColor(level);
  return (
    <span className="inline-block text-[11px] font-semibold px-1.5 py-0.5 rounded-sm whitespace-nowrap" style={{ color: c, background: riskLevelTint(level), border: `1px solid ${c}33` }}>
      {riskLevelLabel(level)}{score != null && <span className="font-normal opacity-75 ml-1">{formatScore(score)}</span>}
    </span>
  );
}

const LIKERT_TEXT: Record<string, string> = { Very_low: "Very low", Low: "Low", Moderate: "Moderate", High: "High", Very_high: "Very high" };

/** A 5-point rating where "good" can be high (readiness) or low (difficulty). */
export function Likert({ value, goodWhen }: { value: string | null; goodWhen: "high" | "low" }) {
  if (!value) return <span className="text-xs text-ink-400">—</span>;
  const order = ["Very_low", "Low", "Moderate", "High", "Very_high"];
  const i = order.indexOf(value);
  const badness = goodWhen === "high" ? 4 - i : i; // 0 good … 4 bad
  const color = badness >= 3 ? riskLevelColor("Very_high") : badness === 2 ? riskLevelColor("Moderate") : riskLevelColor("Very_low");
  return (
    <span className="inline-flex items-center gap-1 text-[11px] whitespace-nowrap" style={{ color }}>
      <span className="flex gap-[2px]" aria-hidden>
        {order.map((_, k) => <span key={k} className="w-1.5 h-2.5 rounded-[1px]" style={{ background: k <= i ? color : "#e5e7eb" }} />)}
      </span>
      {LIKERT_TEXT[value] ?? value}
    </span>
  );
}

/** One-line finding: severity, title, action; expands to detail + evidence. */
export function FindingRow({ insight, names }: { insight: Insight; names: Map<string, string> }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="py-2" data-testid={`finding-${insight.id}`}>
      <button type="button" className="w-full text-left flex gap-2.5 group" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <SeverityDot severity={insight.severity} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-ink-900 font-medium leading-snug group-hover:text-accent">{insight.title}</span>
          <span className="block text-xs mt-0.5" style={{ color: NAVY }}>→ {insight.action.title}{insight.evidence.count > 0 && <span className="text-ink-400"> · {insight.evidence.count} asset{insight.evidence.count === 1 ? "" : "s"}</span>}</span>
        </span>
        <span className="text-ink-300 text-xs mt-0.5" aria-hidden>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="ml-[18px] mt-1.5 text-xs text-ink-600 space-y-1.5">
          <p>{insight.detail}</p>
          <div className="flex items-center gap-2"><ConfidenceChip level={insight.confidence.level} basis={insight.confidence.basis} /><span className="text-ink-400">{insight.confidence.basis}</span></div>
          {insight.evidence.count > 0 && (
            <p className="text-ink-500">{insight.evidence.asset_ids.slice(0, 12).map((id) => names.get(id) ?? id).join(" · ")}{insight.evidence.count > 12 && ` · +${insight.evidence.count - 12} more`}</p>
          )}
        </div>
      )}
    </li>
  );
}

export function money(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}bn`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (n >= 1e3) return `${Math.round(n / 1e3)}k`;
  return String(n);
}

export function costRange(low: number, high: number | null, currency: string | null): string {
  const c = currency ? `${currency} ` : "";
  const range = high === null ? `at least ${c}${money(low)}` : `${c}${money(low)}–${money(high)}`;
  return currency ? range : `${range} (currency not set)`;
}

/** A register cost band: an open-ended top band (assumed upper end) is marked as needing a quote. */
export function costBandText(c: { low: number; high: number | null; assumed_high?: boolean }, currency: string | null): string {
  return c.assumed_high ? `${costRange(c.low, null, currency).replace(/^at least /, "")}+ (quote needed)` : costRange(c.low, c.high, currency);
}

/**
 * A wave's dates as the reports word them: "start now · complete by Mar 2028" or "start by Jan 2027 ·
 * complete by Jan 2029". Runs made before start dates existed carry only target_date (then a start date).
 */
export function waveDateLabel(w: { target_date: string; start_date?: string; start_now?: boolean }): { start: string; complete: string | null } {
  if (!w.start_date) return { start: `start by ${shortDate(w.target_date)}`, complete: null };
  return { start: w.start_now ? "start now" : `start by ${shortDate(w.start_date)}`, complete: `complete by ${shortDate(w.target_date)}` };
}

/** The owner a register row shows: the owning role, else the named person, else the recorded contact. */
export function ownerDisplay(o: { role: string | null; person?: string | null; contact?: string | null }): string | null {
  return o.role || o.person || o.contact || null;
}

export function shortDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}
